import {
  Box,
  Button,
  Dialog,
  DialogContent,
  FormLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { updateEmergencyStatus } from "api/services";
import textCapitalization from "components/textCapitalization";
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";

const STATUS_OPTIONS = ["CONTACTED", "VISITED", "COMPLETED"];

interface Props {
  open: boolean;
  alertId?: string;
  currentStatus?: string;
  onClose: () => void;
}

export const UpdateStatusDialog = ({
  open,
  alertId,
  currentStatus,
  onClose,
}: Props) => {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState("");
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) {
      setStatus("");
      setReason("");
    }
  }, [open]);

  const { mutate, isLoading } = useMutation(updateEmergencyStatus, {
    onSuccess: (res: any) => {
      queryClient.invalidateQueries("emergencyAlerts");
      queryClient.invalidateQueries("emergencyAlertById");
      toast.success(res?.data?.message);
      onClose();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message);
    },
  });

  const handleSave = () => {
    if (!status) {
      toast.error("Please select a status");
      return;
    }
    mutate({ id: alertId, data: { status, reason } });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { borderRadius: "16px", width: 460, maxWidth: "92vw" } }}
    >
      <DialogContent sx={{ p: 4 }}>
        <Typography variant="h4">Update Status</Typography>
        <Typography variant="body2" sx={{ color: "#8288A6", mt: 0.5, mb: 2.5 }}>
          Current status: {textCapitalization(currentStatus || "Pending")}
        </Typography>

        <FormLabel sx={{ fontSize: "13px", mb: 0.5, display: "block" }}>
          New Status
        </FormLabel>
        <Select
          fullWidth
          size="small"
          displayEmpty
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          renderValue={(v: any) =>
            v ? (
              textCapitalization(v)
            ) : (
              <span style={{ color: "#8288A6" }}>Select status</span>
            )
          }
          sx={{ mb: 2.5, borderRadius: "10px" }}
        >
          {STATUS_OPTIONS.map((s) => (
            <MenuItem key={s} value={s}>
              {textCapitalization(s)}
            </MenuItem>
          ))}
        </Select>

        <FormLabel sx={{ fontSize: "13px", mb: 0.5, display: "block" }}>
          Reason / Notes
        </FormLabel>
        <TextField
          fullWidth
          multiline
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Add a specific reason or note for this update"
        />

        <Box display="flex" justifyContent="flex-end" gap={1.5} mt={3}>
          <Button variant="outlined" color="primary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={isLoading}
          >
            Update
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateStatusDialog;
